import {AccessRule} from "../model";
import {Feature} from "@luciad/ria/model/feature/Feature.js";
import {Polygon} from "@luciad/ria/shape/Polygon.js";
import {ShapeType} from "@luciad/ria/shape/ShapeType.js";
import {ShapeList} from "@luciad/ria/shape/ShapeList.js";
import {Point} from "@luciad/ria/shape/Point.js";
import {createComplexPolygon, createPoint, createPolygon} from "@luciad/ria/shape/ShapeFactory.js";
import {DRAW_ID} from "../common/ui/riamap/RiaApi";
import {getReference, parseWellKnownText} from "@luciad/ria/reference/ReferenceProvider.js";
import {ComplexPolygon} from "@luciad/ria/shape/ComplexPolygon.js";
import * as epsgIndexAll from "epsg-index/all.json";
import {CoordinateReference} from "@luciad/ria/reference/CoordinateReference.js";
import * as TransformationFactory from "@luciad/ria/transformation/TransformationFactory.js";
import * as lodash from "lodash";

const allEpsg = epsgIndexAll;

export enum OverlappingShapesType {
  INCLUDE = "INCLUDE",
  EXCLUDE = "EXCLUDE",
  CLIP = "CLIP",
}

export enum SpatialAreaAcceptType {
  INSIDE = "INSIDE",
  OUTSIDE = "OUTSIDE",
}

export enum PermissionsType {
  READ = "READ",
  UPDATE = "UPDATE",
  DELETE = "DELETE",
  MANAGE = "MANAGE",
}

export enum GeneratedExtentType {
  BOUNDS = "BOUNDS",
  PRECISE = "PRECISE",
  ACTIVE = "ACTIVE",
}

export interface SecurityFormAccessRule extends AccessRule {
  isNew: boolean;
  scaleRangeEnabled: boolean;
  spatialRangeEnabled: boolean;
  applyToChildren: boolean;
}

export const convertAccessRulesToSecurityFormAccessRules = (accessRules: AccessRule[]): SecurityFormAccessRule[] => {
  return accessRules.map(pp => {
    return {
      ...pp,
      isNew: false,
      scaleRangeEnabled: !!pp.scaleRange,
      spatialRangeEnabled: !!pp.spatialRange,
      applyToChildren: false
    } as SecurityFormAccessRule;
  })
}

export const convertSecurityFormAccessRuleToAccessRule = (securityFormAccessRule: SecurityFormAccessRule): AccessRule => {
  const accessRule = {
    role: securityFormAccessRule.role,
    permissions: securityFormAccessRule.permissions,
  };
  if (securityFormAccessRule.id) {
    accessRule["id"] = securityFormAccessRule.id;
  }
  if (securityFormAccessRule.scaleRangeEnabled && securityFormAccessRule.scaleRange) {
    accessRule["scaleRange"] = {
      minScaleDenominator: securityFormAccessRule.scaleRange.minScaleDenominator,
      maxScaleDenominator: securityFormAccessRule.scaleRange.maxScaleDenominator
    }
  }
  if (securityFormAccessRule.spatialRangeEnabled && securityFormAccessRule.spatialRange) {
    accessRule["spatialRange"] = securityFormAccessRule.spatialRange;
  }
  return accessRule;
}

export const convertSecurityFormAccessRulesToAccessRules = (securityFormAccessRules: SecurityFormAccessRule[]): AccessRule[] => {
  const accessRules = [];
  securityFormAccessRules.forEach((p: SecurityFormAccessRule) => {
    accessRules.push(convertSecurityFormAccessRuleToAccessRule(p));
  });
  return accessRules;
}

export const getFeatureFromWorkingSet = (workingSet: Feature[]): Feature => {
  if (!workingSet || workingSet.length === 0) {
    return null;
  }
  if (workingSet.length === 1) {
    return lodash.cloneDeep(workingSet[0]);
  }
  const ref4326 = getReference("EPSG:4326");
  const polygons: Polygon[] = [];
  for (const feature of workingSet) {
    getPolygonArrayFromFeature(feature).forEach((polygon) => {
      polygons.push(polygon);
    });
  }
  const multipolygon = createComplexPolygon(ref4326, polygons);
  return new Feature(multipolygon, {}, DRAW_ID);
}

export const getFeatureFromGeometryString = (geometryString: string): Feature => {
  if (!geometryString) {
    return null;
  }

  //regex gets the coordinates of the polygons
  const polygonCoordinates = geometryString.match(/\(\(([^()]+)\)\)/g);
  const ref4326 = getReference("EPSG:4326");

  const polygons: Polygon[] = [];

  if (polygonCoordinates) {
    for (const polygon of polygonCoordinates) {
      //remove the first and last parentheses then split by comma or space
      let coordinateStrings: string[] = polygon.replace(/[()]/g, "").split(/[,\s]/g);
      coordinateStrings = coordinateStrings.filter((s) => s.trim() !== "");
      const coordinates = coordinateStrings.map(Number);
      const points: Point[] = [];
      for (let i = 0; i < coordinates.length; i += 2) {
        points.push(createPoint(ref4326, [coordinates[i], coordinates[i + 1]]));
      }
      polygons.push(createPolygon(ref4326, points));
    }
  }
  if (polygons.length === 0) {
    return null;
  }
  if (polygons.length === 1) {
    return new Feature(polygons[0], {}, DRAW_ID);
  }
  const multipolygon = createComplexPolygon(ref4326, polygons);
  return new Feature(multipolygon, {}, DRAW_ID);
}

export const getSinglePolygonFeatureFromFeature = (feature: Feature): Feature => {
  const polygons = getPolygonArrayFromFeature(feature);
  if (polygons.length === 0) {
    return null;
  }

  return new Feature(polygons[0], {}, DRAW_ID);
}

export const getGeometryStringFromFeature = (feature: Feature): string => {
  let geometryString = "";
  const polygons = getPolygonArrayFromFeature(feature);

  if (polygons.length === 0) {
    return geometryString;
  }

  if (polygons.length > 1) {
    geometryString = "MULTIPOLYGON (";
  } else {
    geometryString = "POLYGON ";
  }
  for (let i = 0; i < polygons.length; i++) {
    const shape = polygons[i];
    if (shape.type === ShapeType.POLYGON) {
      const selectedPolygon = shape as Polygon;
      geometryString += "((";
      for (let j = 0; j < selectedPolygon.pointCount; j++) {
        const prevPoint = j > 0 ? selectedPolygon.getPoint(j - 1) : null;
        const point = selectedPolygon.getPoint(j);
        if (!prevPoint || ((prevPoint.x !== point.x) && (prevPoint.y !== point.y))) {
          geometryString += `${point.x} ${point.y},`;
        }
      }
      const endPoint = selectedPolygon.getPoint(0);
      geometryString += `${endPoint.x} ${endPoint.y}`;
      geometryString += "))";
    }
    if (i < polygons.length - 1) {
      geometryString += ",";
    }
  }
  if (polygons.length > 1) {
    geometryString += ")";
  }

  return geometryString;
}

export const validEPSGCode = (code: string): number => {
  if (!code.toLowerCase().startsWith("epsg:")) {
    return null;
  }
  const referenceId: number = parseInt(code.substring(5), 10);
  if (isNaN(referenceId)) {
    return null;
  }
  const referenceWKT = allEpsg[referenceId]?.wkt;
  if (!referenceWKT) {
    return null;
  }
  const reference = parseWellKnownText(referenceWKT);
  if (reference?.identifier) {
    return referenceId;
  } else {
    return null;
  }
}

const getPolygonArrayFromFeature = (feature: Feature): Polygon[] => {
  const polygons: Polygon[] = [];
  if (!feature?.shape) {
    return polygons;
  }

  const ref4326 = getReference("EPSG:4326");
  if (feature.shape.type === ShapeType.POLYGON) {
    const polygon = feature.shape as Polygon;
    if (polygon.reference !== ref4326) {
      polygons.push(transformPolygonTo4326(polygon));
    } else {
      polygons.push(polygon);
    }
  }
  if (feature.shape.type === ShapeType.COMPLEX_POLYGON) {
    const complexPolygon = feature.shape as ComplexPolygon;
    for (let i = 0; i < complexPolygon.polygonCount; i++) {
      const polygon = complexPolygon.getPolygon(i);
      if (polygon.reference !== ref4326) {
        polygons.push(transformPolygonTo4326(polygon));
      } else {
        polygons.push(polygon);
      }
    }
  }
  if (feature.shape.type === ShapeType.SHAPE_LIST) {
    const shapeList = feature.shape as ShapeList;
    for (let i = 0; i < shapeList.shapeCount; i++) {
      const shape = shapeList.getShape(i);
      if (shape.type === ShapeType.POLYGON) {
        const polygon = shape as Polygon;
        if (polygon.reference !== ref4326) {
          polygons.push(transformPolygonTo4326(polygon));
        } else {
          polygons.push(polygon);
        }
      }
    }
  }
  return polygons;
}

const transformPolygonTo4326 = (polygon: Polygon): Polygon => {
  const ref4326 = getReference("EPSG:4326");
  const reference: CoordinateReference = polygon.reference;
  const points: Point[] = [];

  for (let i = 0; i < polygon.pointCount; i++) {
    const point = polygon.getPoint(i);
    const trans4326 = TransformationFactory.createTransformation(reference, ref4326);
    const point4326 = trans4326.transform(point);
    points.push(createPoint(ref4326, [point4326.x, point4326.y]));
  }
  return createPolygon(ref4326, points);
}

export const mergeFeatures = (features: Feature[]): Feature => {
  const ref4326 = getReference("EPSG:4326");
  const polygons: Polygon[] = [];
  for (const feature of features) {
    getPolygonArrayFromFeature(feature).forEach((polygon) => {
      if (feature.shape.reference !== ref4326) {
        polygons.push(transformPolygonTo4326(polygon));
      } else {
        polygons.push(polygon);
      }
    });
  }
  const multipolygon = createComplexPolygon(ref4326, polygons);
  return new Feature(multipolygon, {}, DRAW_ID);
}